import type { PositionRow } from '../types';

export interface StrategyGroup {
  strategyId: string;
  strategyName: string;
  positions: PositionRow[];
  openCount: number;
  pendingCount: number;
  rejectedCount: number;
  /** Legs where entry is armed / awaiting trigger (TRIGGER_PENDING). */
  triggerPendingCount: number;
  /** Axis-based tally: per-leg count of entry/SL/exit order_status === OPEN. */
  openOrdersCount: number;
  /** Axis-based tally: per-leg count of entry/SL/exit order_status === REJECTED. */
  rejectedAxisCount: number;
  /** Axis-based tally: per-leg count of entry/SL/exit order_status === CANCELLED. */
  cancelledAxisCount: number;
  closedCount: number;
  /** Legs whose backend `missed_exit` field is present (today-only, OPEN past
   *  scheduled exit OR EXIT_PENDING stuck > threshold). Feeds the Missed box
   *  in StatusBoxRow at the strategy + basket header level so the Positions
   *  view surfaces the same red-blink signal as the Schedule view. */
  missedCount: number;
  pnl: number;
}

export interface BasketGroup {
  basketId: string;
  basketName: string;
  /** True when any position in this basket carries `isMarketplace`. Drives
   *  violet styling + MP pill on basket and strategy headers. */
  isMarketplace: boolean;
  strategies: StrategyGroup[];
  openCount: number;
  pendingCount: number;
  rejectedCount: number;
  triggerPendingCount: number;
  openOrdersCount: number;
  rejectedAxisCount: number;
  cancelledAxisCount: number;
  closedCount: number;
  /** Sum of strategy missedCount across this basket. */
  missedCount: number;
  pnl: number;
  totalPositions: number;
}

function legTriggerPendingCount(p: PositionRow): number {
  // Only entry has TRIGGER_PENDING as an "awaiting" signal (WAT / ORB).
  // SL's TRIGGER_PENDING is the healthy armed state and is not a user action.
  // Exit has no TRIGGER_PENDING status.
  //
  // PENDING_PLACE (Phase 6, claim-then-place in flight) folds into this
  // bucket — the entry is functionally "awaiting broker landing" for the
  // ~1-3s claim window. Matches computeEntryChip's pending branch and
  // backend `_tally_entry_status` which groups them together.
  //
  // UL_MONITOR_PENDING (Phase 5b) — UL-W&T invisible-wait, no broker order;
  // the underlying monitor is polling spot. Same "armed, awaiting" semantic.
  return (
    p.entryStatus === 'TRIGGER_PENDING'
    || p.entryStatus === 'PENDING_PLACE'
    || p.entryStatus === 'UL_MONITOR_PENDING'
  ) ? 1 : 0;
}

function legOpenOrdersCount(p: PositionRow): number {
  let n = 0;
  if (p.entryStatus === 'OPEN') n++;
  if (p.slStatus === 'OPEN') n++;
  if (p.exitStatus === 'OPEN') n++;
  return n;
}

function legRejectedAxisCount(p: PositionRow): number {
  let n = 0;
  if (p.entryStatus === 'REJECTED') n++;
  if (p.slStatus === 'REJECTED') n++;
  if (p.exitStatus === 'REJECTED') n++;
  return n;
}

function legCancelledAxisCount(p: PositionRow): number {
  let n = 0;
  if (p.entryStatus === 'CANCELLED') n++;
  if (p.slStatus === 'CANCELLED') n++;
  if (p.exitStatus === 'CANCELLED') n++;
  return n;
}

/** Nest a flat position list into baskets → strategies → legs. */
export function buildNestedGroups(positions: PositionRow[]): BasketGroup[] {
  const basketMap = new Map<string, BasketGroup>();

  for (const p of positions) {
    if (!basketMap.has(p.basketId)) {
      basketMap.set(p.basketId, {
        basketId: p.basketId,
        basketName: p.basketName || p.basketId,
        isMarketplace: false,
        strategies: [],
        openCount: 0,
        pendingCount: 0,
        rejectedCount: 0,
        triggerPendingCount: 0,
        openOrdersCount: 0,
        rejectedAxisCount: 0,
        cancelledAxisCount: 0,
        closedCount: 0,
        missedCount: 0,
        pnl: 0,
        totalPositions: 0,
      });
    }
    const basket = basketMap.get(p.basketId)!;
    // Any marketplace position in the basket flags the whole basket. All
    // strategies in a marketplace basket are marketplace strategies by data
    // model (allocation.source=MARKETPLACE is at basket-allocation scope),
    // so this is a safe OR-reduce.
    if (p.isMarketplace) basket.isMarketplace = true;
    const tp = legTriggerPendingCount(p);
    const oo = legOpenOrdersCount(p);
    const ra = legRejectedAxisCount(p);
    const ca = legCancelledAxisCount(p);
    // `p.missedExit` is set by `scheduleToPositionRow` when the backend stamps
    // `missed_exit` on today's view — absent on healthy positions and on rows
    // from the Positions page proper (which doesn't carry the field). The
    // boolean coerce + 1/0 is enough; one missed_exit = one missed leg.
    const me = p.missedExit ? 1 : 0;
    basket.totalPositions++;
    basket.pnl += p.pnl ?? 0;
    if (p.status === 'OPEN' || p.status === 'EXIT_PENDING') basket.openCount++;
    if (p.status === 'ENTRY_PENDING' && p.entryStatus !== 'REJECTED') basket.pendingCount++;
    if (p.entryStatus === 'REJECTED') basket.rejectedCount++;
    if (p.status === 'CLOSED') basket.closedCount++;
    basket.triggerPendingCount += tp;
    basket.openOrdersCount += oo;
    basket.rejectedAxisCount += ra;
    basket.cancelledAxisCount += ca;
    basket.missedCount += me;

    let strategy = basket.strategies.find((s) => s.strategyId === p.strategyId);
    if (!strategy) {
      strategy = {
        strategyId: p.strategyId,
        strategyName: p.strategyName || p.strategyId,
        positions: [],
        openCount: 0,
        pendingCount: 0,
        rejectedCount: 0,
        triggerPendingCount: 0,
        openOrdersCount: 0,
        rejectedAxisCount: 0,
        cancelledAxisCount: 0,
        closedCount: 0,
        missedCount: 0,
        pnl: 0,
      };
      basket.strategies.push(strategy);
    }
    strategy.positions.push(p);
    strategy.pnl += p.pnl ?? 0;
    if (p.status === 'OPEN' || p.status === 'EXIT_PENDING') strategy.openCount++;
    if (p.status === 'ENTRY_PENDING' && p.entryStatus !== 'REJECTED') strategy.pendingCount++;
    if (p.entryStatus === 'REJECTED') strategy.rejectedCount++;
    if (p.status === 'CLOSED') strategy.closedCount++;
    strategy.triggerPendingCount += tp;
    strategy.openOrdersCount += oo;
    strategy.rejectedAxisCount += ra;
    strategy.cancelledAxisCount += ca;
    strategy.missedCount += me;
  }

  // Stable order: by leg_index when available, else entryTime
  for (const basket of Array.from(basketMap.values())) {
    for (const strat of basket.strategies) {
      strat.positions.sort((a, b) => {
        const aTime = a.entryFilledAt || a.entryTime || '';
        const bTime = b.entryFilledAt || b.entryTime || '';
        return bTime.localeCompare(aTime); // latest first
      });
    }
    basket.strategies.sort((a, b) => a.strategyName.localeCompare(b.strategyName));
  }

  return Array.from(basketMap.values()).sort((a, b) => a.basketName.localeCompare(b.basketName));
}
